import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Raktár szabálykönyv — DTO-k a 23-logic.js ruleVals/salesData state-jéből
// levezetve (14-raktar-szabaly.html).
// Alapszint kalkuláció: minLevel = max(1, round(avgMonthlySales × factor)),
// ahol avgMonthlySales az utolsó lookbackMonths hónap átlaga.
// ============================================================================

export interface DispatchClassEtsyProfile {
  shopId: number;
  shopName: string;
  shippingProfileId: string | null;
}

export interface DispatchClass {
  id: number;
  /** pl. 'Normál feladás' */
  name: string;
  /** feladási idő min. nap */
  minDays: number;
  /** feladási idő max. nap */
  maxDays: number;
  /**
   * Etsy szállítási profil BOLTONKÉNT — a profil azonosítója bolthoz kötött,
   * ezért minden bekötött boltnál külön kell megadni.
   */
  etsyProfiles: DispatchClassEtsyProfile[];
}

export interface CreateDispatchClassRequest {
  name: string;
  minDays: number;
  maxDays: number;
}

export type UpdateDispatchClassRequest = Partial<CreateDispatchClassRequest>;

export interface WarehouseRuleConfig {
  /** visszatekintés: 1..4 hónap — ruleLookback */
  lookbackMonths: number;
  /** alapszint szorzó: 0.25 | 0.5 | 0.75 | 1 — ruleFactor */
  factor: number;
  /** automatikus feladási osztály hozzárendelés — ruleAutoOn */
  autoAssignEnabled: boolean;
  /** a teljes alapszint-szabály be/ki — kikapcsolva a kézi minimumok a mérvadók */
  ruleEnabled: boolean;
  /** a feladási osztályok kiküldése az Etsy-re (szállítási profil csere) */
  etsySyncEnabled: boolean;
  /** mikor futott le utoljára a teljes újraszámolás (ISO) */
  lastAppliedAt: string | null;
  dispatchClasses: DispatchClass[];
  /** 'Készleten' állapothoz rendelt osztály — ruleTierOk */
  tierOkClassId: number;
  /** 'Minimum szinten' állapothoz — ruleTierMin */
  tierMinClassId: number;
  /** 'Elfogyott' állapothoz — ruleTierOut */
  tierOutClassId: number;
}

export type UpdateWarehouseRuleConfigRequest = Partial<Omit<WarehouseRuleConfig, 'dispatchClasses'>>;

export type WarehouseStockState = 'ok' | 'min' | 'out';
export type LimitSource = 'auto' | 'manual';

export interface WarehouseRulePreviewRow {
  sku: string;
  name: string;
  shop: string | null;
  /** utolsó lookbackMonths hónap eladásai db/hó, legfrissebb elöl — salesData */
  monthlySales: number[];
  /** kerekített átlag */
  avgMonthlySales: number;
  /** a MÉRVADÓ minimum (az automatikus vagy a kézi, l. `source`) */
  minLevel: number;
  /** automatikusan számolt szint; null = nincs elég adat */
  autoLevel: number | null;
  /** kézzel beállított szint */
  manualLevel: number;
  /** melyik a kettő közül a mérvadó */
  source: LimitSource;
  componentId: number;
  /** nincs elegendő adat (30 napnál rövidebb előzmény) — a kézi minimum marad */
  noHistory?: boolean;
  /** hány napnyi eladási előzmény áll rendelkezésre */
  historyDays?: number;
  /** ennyi hónapra osztottuk az átlagot */
  effectiveMonths?: number;
  /** eladott darab az ablakban */
  totalSold?: number;
  /** elérhető: kész db + komponensekből összeállítható db */
  available: number;
  state: WarehouseStockState;
  /** a state-hez az aktuális szabály szerint rendelt feladási osztály */
  dispatchClassId: number;
}

@Injectable({ providedIn: 'root' })
export class WarehouseRulesService {

  constructor(private http: HttpClient) {}

  /** GET warehouse-rules — a mentett szabály-konfiguráció */
  getConfig(): Observable<WarehouseRuleConfig> {
    return this.http.get<WarehouseRuleConfig>('warehouse-rules');
  }

  /** PATCH warehouse-rules — konfiguráció mentés (lookback/factor/auto/tier-ek) */
  updateConfig(request: UpdateWarehouseRuleConfigRequest): Observable<WarehouseRuleConfig> {
    return this.http.patch<WarehouseRuleConfig>('warehouse-rules', request);
  }

  /** POST warehouse-rules/dispatch-classes — új feladási osztály */
  createDispatchClass(request: CreateDispatchClassRequest): Observable<DispatchClass> {
    return this.http.post<DispatchClass>('warehouse-rules/dispatch-classes', request);
  }

  /** PATCH warehouse-rules/dispatch-classes/:id */
  updateDispatchClass(id: number, request: UpdateDispatchClassRequest): Observable<DispatchClass> {
    return this.http.patch<DispatchClass>(`warehouse-rules/dispatch-classes/${id}`, request);
  }

  /** PATCH warehouse-rules/dispatch-classes/:id/etsy-profile — boltonkénti profil */
  setDispatchClassEtsyProfile(
    id: number,
    etsyShopId: number,
    shippingProfileId: string | null,
  ): Observable<DispatchClass> {
    return this.http.patch<DispatchClass>(
      `warehouse-rules/dispatch-classes/${id}/etsy-profile`,
      { etsyShopId, shippingProfileId },
    );
  }

  /** DELETE warehouse-rules/dispatch-classes/:id */
  deleteDispatchClass(id: number): Observable<void> {
    return this.http.delete<void>(`warehouse-rules/dispatch-classes/${id}`);
  }

  /**
   * GET warehouse-rules/preview?lookbackMonths=&factor= — élő előnézet.
   * A query paramok felülírják a mentett konfigot (mentés nélküli próbálgatáshoz);
   * nélkülük a mentett értékekkel számol.
   */
  getPreview(lookbackMonths?: number, factor?: number): Observable<WarehouseRulePreviewRow[]> {
    let params = new HttpParams();
    if (lookbackMonths != null) params = params.set('lookbackMonths', lookbackMonths);
    if (factor != null) params = params.set('factor', factor);
    return this.http.get<WarehouseRulePreviewRow[]>('warehouse-rules/preview', { params });
  }

  /** GET warehouse-rules/components — alkatrészenkénti automatikus / kézi szint */
  getComponentLevels(): Observable<ComponentLevelRow[]> {
    return this.http.get<ComponentLevelRow[]>('warehouse-rules/components');
  }

  /** POST warehouse-rules/recalculate — teljes újraszámolás és érvényesítés */
  recalculate(): Observable<RecalculateResult> {
    return this.http.post<RecalculateResult>('warehouse-rules/recalculate', {});
  }

  /** GET warehouse-rules/etsy-shipping-profiles — a boltok szállítási profiljai */
  getEtsyShippingProfiles(): Observable<EtsyShippingProfile[]> {
    return this.http.get<EtsyShippingProfile[]>('warehouse-rules/etsy-shipping-profiles');
  }

  /** POST warehouse-rules/sync-etsy — a feladási osztályok kiküldése */
  syncEtsy(): Observable<EtsySyncResult> {
    return this.http.post<EtsySyncResult>('warehouse-rules/sync-etsy', {});
  }

  /** PATCH components/:id/dispatch-exempt — komponens kivétele a feladási szabály alól */
  setDispatchExempt(componentId: number, exempt: boolean): Observable<unknown> {
    return this.http.patch(`components/${componentId}/dispatch-exempt`, { exempt });
  }

  /** PATCH components/:id/manual-limit — a kézi minimum állítása */
  setManualLimit(componentId: number, manualLimit: number): Observable<{
    id: number; manualLimit: number; autoLimit: number | null; effectiveLimit: number; source: LimitSource;
  }> {
    return this.http.patch<{
      id: number; manualLimit: number; autoLimit: number | null; effectiveLimit: number; source: LimitSource;
    }>(`components/${componentId}/manual-limit`, { manualLimit });
  }
}

export interface ComponentLevelRow {
  componentId: number;
  sku: string;
  name: string;
  autoLevel: number | null;
  manualLevel: number;
  effectiveLevel: number;
  source: LimitSource;
  available: number;
  state: WarehouseStockState;
  /** ki van véve a feladási szabály alól */
  dispatchRuleExempt: boolean;
  /** hány termék BOM-jában szerepel */
  usedInProducts: number;
}

export interface RecalculateResult {
  products: number;
  components: number;
  limitChanges: number;
  dispatchChanges: number;
  etsy: EtsySyncResult | null;
}

export interface EtsySyncResult {
  enabled: boolean;
  shops: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface EtsyShippingProfile {
  id: string;
  title: string;
  minProcessingDays: number | null;
  maxProcessingDays: number | null;
  originCountryIso: string | null;
  shopId: number;
  shopName: string;
}
